import React from 'react';

/**
 * H4: React Error Boundary 组件
 *
 * 捕获子组件树中的渲染错误，展示降级UI而非白屏。
 * 提供刷新按钮让用户尝试恢复。
 *
 * 用法：
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    /**
     * 从错误中更新状态，触发降级UI渲染
     */
    static getDerivedStateFromError(error) {
        return { hasError: true, error: error };
    }

    /**
     * 记录错误日志（可扩展为上报到监控平台）
     */
    componentDidCatch(error, errorInfo) {
        console.error('[ErrorBoundary] 捕获到渲染错误:', error, errorInfo);
        this.setState({ errorInfo: errorInfo });

        // 可在此处添加错误上报逻辑
        // reportError({ error, errorInfo, timestamp: Date.now() });
    }

    /**
     * 刷新页面尝试恢复
     */
    handleRefresh = () => {
        window.location.reload();
    };

    /**
     * 返回首页
     */
    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // 支持自定义降级UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '40px 20px',
                    backgroundColor: '#ebf1fd',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: '16px'
                    }}>
                        😵
                    </div>
                    <h3 style={{
                        fontSize: '18px',
                        color: '#1f2329',
                        margin: '0 0 8px 0',
                        fontWeight: 600
                    }}>
                        页面加载异常
                    </h3>
                    <p style={{
                        fontSize: '14px',
                        color: '#8f959e',
                        margin: '0 0 24px 0',
                        lineHeight: 1.6,
                        maxWidth: '280px'
                    }}>
                        页面渲染时发生了错误，请刷新页面重试。如果问题持续存在，请联系管理员。
                    </p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            onClick={this.handleRefresh}
                            style={{
                                backgroundColor: '#3370ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 24px',
                                fontSize: '15px',
                                cursor: 'pointer',
                                fontWeight: 500
                            }}
                        >
                            刷新页面
                        </button>
                        <button
                            onClick={this.handleGoHome}
                            style={{
                                backgroundColor: '#f5f6f7',
                                color: '#646a73',
                                border: '1px solid #d0d5dd',
                                borderRadius: '8px',
                                padding: '10px 24px',
                                fontSize: '15px',
                                cursor: 'pointer'
                            }}
                        >
                            返回首页
                        </button>
                    </div>
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <details style={{
                            marginTop: '24px',
                            textAlign: 'left',
                            maxWidth: '320px',
                            fontSize: '12px',
                            color: '#f54a45',
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '12px',
                            wordBreak: 'break-all'
                        }}>
                            <summary>错误详情（开发模式）</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0' }}>
                                {this.state.error.toString()}
                            </pre>
                        </details>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}
